import React, { useState } from 'react';
import _ from 'lodash';
import { Form } from 'components/Form';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Box } from 'components/Wrapped';
import {
  CommissionPercentageInfo,
  CreateOrderBalance,
  CreateOrderField,
  CreatePercentSelectors,
  CreateOrderButton,
  CreateOrderFee,
  BuyLimitInfo,
  ConfirmOrderModal,
} from 'containers/CreateOrder';
import { withNamespaces } from 'react-i18next';
import { nestedTranslate } from 'utils/strings';
import { connect } from 'react-redux';
import { getSingleTradingPairSettings } from 'redux/selectors/settings';

const limitOrderValidationSchema = (minTickSize, minTradeAmount, minOrderValue) => {
  return Yup.object().shape({
    rate: Yup.number()
      .required()
      .min(minTickSize),
    volume: Yup.number()
      .required()
      .min(minTradeAmount),
    total: Yup.number().min(minOrderValue),
  });
};

const LimitOrderFormContainer = (props) => {
  const {
    side,
    tradingPair,
    placeOrder,
    lastPrice,
    t: translate,
    tradingPairSettings: { tickDecimals, tradeAmountDecimals, orderValueDecimals, minTickSize, minTradeAmount, minOrderValue },
    maxOrderValue,
  } = props;
  const t = nestedTranslate(translate, 'forms.limitOrder');
  const additionalValues = {
    market: tradingPair.quoteCurrency,
    trade: tradingPair.baseCurrency,
  };
  const [confirmModalVisibility, setConfirmModalVisibility] = useState(false);

  const onSubmitForm = (values) => {
    placeOrder({ ...values, side, ...additionalValues });
  };

  return (
    <Formik
      initialValues={{
        side,
        volume: '',
        rate: lastPrice || '',
        total: '',
        type: 'LIMIT',
        timeInForce: 'GTC',
      }}
      validateOnChange={true}
      validationSchema={limitOrderValidationSchema(minTickSize, minTradeAmount, minOrderValue)}
      onSubmit={onSubmitForm}
    >
      {(props) => (
        <Form gap="4px" autoComplete="off">
          <CreateOrderBalance tradingPair={tradingPair} side={side} />

          <CreateOrderField
            type="text"
            name="rate"
            label={t('rate')}
            currency={tradingPair.quoteCurrency}
            formProps={props}
            side={side}
            precision={tickDecimals}
          />

          <CreateOrderField
            type="text"
            name="volume"
            label="Сумма"
            currency={tradingPair.baseCurrency}
            formProps={props}
            side={side}
            precision={tradeAmountDecimals}
          />

          <CreatePercentSelectors side={side} total={props.values.total} amount={props.values.volume} />

          <CreateOrderField
            type="text"
            name="total"
            label="Всего"
            currency={tradingPair.quoteCurrency}
            formProps={props}
            side={side}
            precision={orderValueDecimals}
            showErrors={true}
          />

          <Box pad="none" gap="8px" margin={{ top: '8px' }}>
            <BuyLimitInfo currency={tradingPair.baseCurrency} maxOrderValue={maxOrderValue} />
            <CreateOrderFee side={side} tradingPair={tradingPair} total={props.values.rate * props.values.volume} />
          </Box>

          <Box pad="none" margin={{ top: '16px' }}>
            <CreateOrderButton
              side={side}
              tradingPair={tradingPair}
              onCreateOrderButtonClick={() => {
                props.validateForm().then((errors) => {
                  if (_.isEmpty(errors)) {
                    setConfirmModalVisibility(true);
                  }
                });
              }}
            />
          </Box>

          <Box pad="none" margin={{ top: '16px' }}>
            <CommissionPercentageInfo />
          </Box>

          <ConfirmOrderModal
            orderType="Лимит"
            tradeMode={side}
            price={props.values.rate}
            visible={confirmModalVisibility}
            baseCurrency={tradingPair.baseCurrency}
            quoteCurrency={tradingPair.quoteCurrency}
            amount={props.values.volume}
            onToggleModal={() => {
              setConfirmModalVisibility(!confirmModalVisibility);
            }}
            onСonfirmButtonClick={() => {
              props.submitForm().finally(() => {
                setConfirmModalVisibility(false);
              });
            }}
          />
        </Form>
      )}
    </Formik>
  );
};

const mapStateToProps = (state, props) => ({
  tradingPairSettings: getSingleTradingPairSettings(state, props),
});

const LimitOrderForm = withNamespaces()(connect(mapStateToProps)(LimitOrderFormContainer));

const LimitOrder = ({ tradingPair, placeOrder, tradeMode, maxOrderValue, lastPrice }) => {
  return (
    <Box pad={{ vertical: '12px', horizontal: '0px' }}>
      <LimitOrderForm
        tradingPair={tradingPair}
        side={tradeMode === 'buy' ? 'BUY' : 'SELL'}
        placeOrder={placeOrder}
        maxOrderValue={maxOrderValue}
        lastPrice={lastPrice}
      />
    </Box>
  );
};

export default LimitOrder;
